import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment, SupportedLanguage } from '@rms/env';
import { catchError, filter, map, of, switchMap, take, timeout } from 'rxjs';

import { LanguageDetectionService } from './language-detection.service';

// Upper bound on how long navigation may wait for the initial detection to settle.
const LANG_DETECTION_TIMEOUT_MS = 1000;
// How long to wait for applying locale
const LOCALE_APPLY_TIMEOUT_MS = 2000;

/**
 * Applies a `?lang=` query param on top of the detected language, blocking navigation
 * until the translations are loaded so the page never paints in one language and then
 * flips to another.
 */
export const langParamResolver: ResolveFn<void> = (route) => {
  const translateService = inject(TranslateService);
  const languageDetectionService = inject(LanguageDetectionService);

  const param = route.queryParamMap.get('lang');
  // Accept locale-like values (?lang=it-IT) and ignore unsupported ones so we never
  // request a translation file that doesn't exist.
  const lang = param ? param.split('-')[0].toLowerCase() : null;

  if (!lang || !environment.supportedLanguages.some((l: SupportedLanguage) => l.locale === lang)) {
    return;
  }

  // Wait for the initial detection to settle before overriding it, otherwise the two
  // in-flight use() calls race. Bounded and caught: a translation load that never
  // resolves must not hold navigation open forever.
  return languageDetectionService.languageDetectionComplete.pipe(
    filter(Boolean),
    take(1),
    timeout({ first: LANG_DETECTION_TIMEOUT_MS, with: () => of(true) }),
    switchMap(() => {
      return translateService.use(lang).pipe(timeout({ first: LOCALE_APPLY_TIMEOUT_MS, with: () => of(true) }));
    }),
    map(() => undefined),
    catchError(() => of(undefined))
  );
};
